import { Ionicons } from '@expo/vector-icons';
import { useEffect } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { getBodyFont } from '@/constants/typography';

type ChatFABMenuProps = {
  visible: boolean;
  onClose: () => void;
  onAskFim: () => void;
  onAISuggestions: () => void;
  onAddByPhoto: () => void;
  onRoutePlanner: () => void;
  activeRouteProgress: { completed: number; total: number } | null;
  anchorBottom: number;
};

/**
 * ChatFABMenu - Animated popup menu that appears above the ChatFAB.
 * Shows four options: "Ask Fim" (chat), "AI Suggestions" (stacked cards),
 * "Add by photo" (extract tasks/events from a photo), and "Route Planner"
 * (reads "Today's Route" with progress while a plan is active).
 */
export function ChatFABMenu({
  visible,
  onClose,
  onAskFim,
  onAISuggestions,
  onAddByPhoto,
  onRoutePlanner,
  activeRouteProgress,
  anchorBottom,
}: ChatFABMenuProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const { width: screenWidth } = useWindowDimensions();
  const menuLeft = (screenWidth / 2) - 90; // menu is 180px wide

  const scale = useSharedValue(0);
  const opacity = useSharedValue(0);

  useEffect(() => {
    const easing = Easing.out(Easing.quad);
    if (visible) {
      scale.value = withTiming(1, { duration: 75, easing });
      opacity.value = withTiming(1, { duration: 75, easing });
    } else {
      scale.value = withTiming(0, { duration: 75, easing });
      opacity.value = withTiming(0, { duration: 75, easing });
    }
  }, [visible, scale, opacity]);

  const menuStyle = useAnimatedStyle(() => ({
    transform: [{ scaleY: scale.value }, { scaleX: scale.value }],
    opacity: opacity.value,
  }));

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: opacity.value * 0.3,
  }));

  const handlePress = (action: () => void) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    action();
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <Pressable style={StyleSheet.absoluteFill} onPress={onClose}>
        <Animated.View style={[styles.backdrop, backdropStyle]} />
      </Pressable>

      {/* Menu */}
      <Animated.View
        style={[
          styles.menu,
          {
            backgroundColor: palette.cardBackground,
            bottom: anchorBottom + 70,
            left: menuLeft,
          },
          menuStyle,
        ]}
      >
        <TouchableOpacity
          style={styles.menuItem}
          onPress={() => handlePress(onAskFim)}
          activeOpacity={0.7}
        >
          <View style={[styles.menuIcon, { backgroundColor: palette.primary + '20' }]}>
            <Ionicons name="chatbubble-ellipses" size={20} color={palette.primary} />
          </View>
          <Text style={[styles.menuLabel, { color: palette.text, fontFamily: getBodyFont() }]}>
            Chat with Fim
          </Text>
        </TouchableOpacity>

        <View style={[styles.separator, { backgroundColor: palette.borderMuted }]} />

        <TouchableOpacity
          style={styles.menuItem}
          onPress={() => handlePress(onAISuggestions)}
          activeOpacity={0.7}
        >
          <View style={[styles.menuIcon, { backgroundColor: palette.primary + '20' }]}>
            <Ionicons name="sparkles" size={20} color={palette.primary} />
          </View>
          <Text style={[styles.menuLabel, { color: palette.text, fontFamily: getBodyFont() }]}>
            Suggestions
          </Text>
        </TouchableOpacity>

        <View style={[styles.separator, { backgroundColor: palette.borderMuted }]} />

        <TouchableOpacity
          style={styles.menuItem}
          onPress={() => handlePress(onAddByPhoto)}
          activeOpacity={0.7}
        >
          <View style={[styles.menuIcon, { backgroundColor: palette.primary + '20' }]}>
            <Ionicons name="camera" size={20} color={palette.primary} />
          </View>
          <Text style={[styles.menuLabel, { color: palette.text, fontFamily: getBodyFont() }]}>
            Add by photo
          </Text>
        </TouchableOpacity>

        <View style={[styles.separator, { backgroundColor: palette.borderMuted }]} />

        <TouchableOpacity
          style={styles.menuItem}
          onPress={() => handlePress(onRoutePlanner)}
          activeOpacity={0.7}
        >
          <View style={[styles.menuIcon, { backgroundColor: palette.primary + '20' }]}>
            <Ionicons
              name={activeRouteProgress ? 'navigate' : 'map'}
              size={20}
              color={palette.primary}
            />
          </View>
          <View style={styles.menuLabelColumn}>
            <Text style={[styles.menuLabel, { color: palette.text, fontFamily: getBodyFont() }]}>
              {activeRouteProgress ? "Today's Route" : 'Route Planner'}
            </Text>
            {activeRouteProgress && (
              <Text style={[styles.menuSubLabel, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
                {activeRouteProgress.completed} of {activeRouteProgress.total}
              </Text>
            )}
          </View>
        </TouchableOpacity>
      </Animated.View>
    </>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#000',
  },
  menu: {
    position: 'absolute',
    transformOrigin: 'center bottom',
    width: 180,
    borderRadius: BorderRadius.lg,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 10,
    zIndex: 200,
    paddingVertical: Spacing.xs,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    gap: Spacing.md,
  },
  menuIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuLabel: {
    fontSize: 15,
    fontWeight: '600',
  },
  menuLabelColumn: {
    flexShrink: 1,
  },
  menuSubLabel: {
    fontSize: 12,
  },
  separator: {
    height: 1,
    marginHorizontal: Spacing.lg,
  },
});
